import { useAgentStates } from '../hooks/useAgentStates';
import { AGENTS, AGENTS_BY_ID } from '../lib/agents';
import type { AgentState } from '../lib/schema';

interface Props {
  readonly path: string;
}

function StatusLine({ state }: { state: AgentState }) {
  const label = state.status;
  return (
    <>
      <span className={`status-dot status-${label}`} aria-hidden="true" />
      <strong>{label}</strong>
      {state.activity ? ` — ${state.activity}` : null}
    </>
  );
}

export function TextView({ path }: Props) {
  const { states, lastSync } = useAgentStates();

  const agentSlug = path.match(/^\/text\/agents\/([a-z0-9-]+)$/i)?.[1];
  if (agentSlug && AGENTS_BY_ID[agentSlug as keyof typeof AGENTS_BY_ID]) {
    const cfg = AGENTS_BY_ID[agentSlug as keyof typeof AGENTS_BY_ID];
    const state = states.find((s) => s.agentId === cfg.id);
    return (
      <article className="text-view">
        <nav>
          <a href="/text">← All agents</a> · <a href="/">Station view</a>
        </nav>
        <h1>{cfg.label}</h1>
        <p>
          <em>{cfg.title} · {cfg.businessUnit}</em>
        </p>
        <p>{cfg.dossier}</p>
        <h2>Live status</h2>
        {state ? (
          <p>
            <StatusLine state={state} />
          </p>
        ) : (
          <p>No state received.</p>
        )}
        {state?.kpis && (
          <>
            <h3>KPIs</h3>
            <ul>
              {Object.entries(state.kpis).map(([k, v]) => (
                <li key={k}>
                  <code>{k}</code>: {v.toLocaleString()}
                </li>
              ))}
            </ul>
          </>
        )}
        <h3>Runtime</h3>
        <p>
          Status URL: <code>{cfg.statusUrl}</code>
        </p>
      </article>
    );
  }

  return (
    <article className="text-view">
      <header>
        <nav aria-label="Primary">
          <a href="/">Station view</a> · <a href="/text">Text view</a> ·{' '}
          <a href="/record">Record</a>
        </nav>
        <h1>Command Deck</h1>
        <p>
          An accessible, semantic view of Jarius Curry&rsquo;s AI agent operation.
          Every agent exposes a <code>/status</code> URL. This page fans out to read
          them every 3 seconds.
        </p>
        {lastSync && (
          <p>
            <small>Last synced {new Date(lastSync).toLocaleTimeString()}.</small>
          </p>
        )}
      </header>

      <h2>Portfolio C-Suite</h2>
      {AGENTS.filter((a) => a.businessUnit === 'portfolio').map((cfg) => {
        const state = states.find((s) => s.agentId === cfg.id);
        return (
          <section key={cfg.id} aria-labelledby={`agent-${cfg.id}`}>
            <h3 id={`agent-${cfg.id}`}>
              <a href={`/text/agents/${cfg.id}`}>{cfg.label}</a> — {cfg.title}
            </h3>
            <p>{cfg.dossier}</p>
            {state && (
              <p>
                <StatusLine state={state} />
              </p>
            )}
          </section>
        );
      })}

      <h2>Business Unit Leads</h2>
      {AGENTS.filter((a) => a.businessUnit !== 'portfolio').map((cfg) => {
        const state = states.find((s) => s.agentId === cfg.id);
        return (
          <section key={cfg.id} aria-labelledby={`agent-${cfg.id}`}>
            <h3 id={`agent-${cfg.id}`}>
              <a href={`/text/agents/${cfg.id}`}>{cfg.label}</a> — {cfg.title} ·{' '}
              {cfg.businessUnit}
            </h3>
            <p>{cfg.dossier}</p>
            {state && (
              <p>
                <StatusLine state={state} />
              </p>
            )}
          </section>
        );
      })}
    </article>
  );
}
